import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ErrorMessage, Form, Formik } from 'formik';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';

import { addAgentProvision, create, deleteAgentProvision, evaluate, getById, update, updateAgentProvision } from 'app/api/provisionApi';
import { ProgressSpinner } from 'primereact/progressspinner';
import * as Yup from 'yup';
import { InputNumber } from 'primereact/inputnumber';
import DateTextInput from 'app/component/date-text-input';
import ContractWidget from 'app/component/contract-widget';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { ToastContext } from 'app/context/ToastContext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import AgentWidget from 'app/component/agent-widget';

const validationSchema = Yup.object({
  type: Yup.number().required('Toto je povinný údaj'),
  from: Yup.date().required('Toto je povinný údaj'),
  to: Yup.date().required('Toto je povinný údaj'),
  zaklad: Yup.number().required('Toto je povinný údaj'),
  provizia: Yup.number().required('Toto je povinný údaj'),
  contract: Yup.object().required('Toto je povinný údaj'),
});

const provisionTypes = [
  { value: 2, label: 'následná' },
  { value: 1, label: 'získateľská' },
];

const riskTypes = [
  { value: 1, label: 'hlavné riziko' },
  { value: 2, label: 'vedľajšie riziká' },
  { value: 3, label: 'Vedľajšie riziká 2' },
];

const CreateProvision = () => {
  const { id } = useParams();
  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const [provision, setProvision] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showSuccess, showError } = useContext(ToastContext);
  const [agentProvisions, setAgentProvisions] = useState([]);
  const [showAgentProvisionDialog, setShowAgentProvisionDialog] = useState(false);
  const [editAgentProvision, setEditAgentProvision] = useState(null);
  const [deleteProvisionDialog, setDeleteProvisionDialog] = useState(false);

  function setResponseToData(response) {
    if (response.data) {
      setProvision(response.data);
      setAgentProvisions(response.data.provisionsForAgents);
    }
  }

  useEffect(() => {
    if (id) {
      getById(id)
        .then(pr => {
          setResponseToData(pr);
        })
        .catch(e => {
          console.error(e);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [id]);

  const handleSubmit = values => {
    setLoading(true);
    if (id) {
      update(id, values)
        .then(r => {
          setResponseToData(r);
          showSuccess('Úspech', 'Data úspešne uložené.');
        })
        .catch(e => {
          console.error(e);
          showError('Chyba', e.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      create(values)
        .then(r => {
          setResponseToData(r);
          showSuccess('Úspech', 'Data úspešne uložené.');
        })
        .catch(e => {
          console.error(e);
          showError('Chyba', e.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    }
  };

  const onEvaluate = values => {
    setLoading(true);
    evaluate(id, values)
      .then(r => {
        setResponseToData(r);
        showSuccess('Úspech', 'Data úspešne spracované.');
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const onCancel = () => {
    navigate(`/provisions?p=${tempParameters}`);
  };

  const cellEditor = options => {
    if (options.field === 'periodFrom' || options.field === 'periodTo') return dateEditor(options);
    if (options.field === 'amount' || options.field === 'fee') return priceEditor(options);
    else return textEditor(options);
  };

  const dateEditor = options => {
    return (
      <DateTextInput
        value={options.value?.length === 10 ? options.value : ''}
        onChange={val => options.editorCallback(val)}
      />
    );
  };

  const textEditor = options => {
    return (
      <InputText
        type="text"
        value={options.value}
        onChange={e => options.editorCallback(e.target.value)}
        onKeyDown={e => e.stopPropagation()}
      />
    );
  };

  const priceEditor = options => {
    return (
      <InputNumber
        value={options.value}
        onValueChange={e => options.editorCallback(e.value)}
        locale="sk"
        minFractionDigits={0}
        maxFractionDigits={2}
        onKeyDown={e => e.stopPropagation()}
      />
    );
  };

  const openDialog = ap => {
    setEditAgentProvision(ap);
    setShowAgentProvisionDialog(true);
  };

  const renderHeader = () => {
    return (
      <div className="flex justify-content-between">
        <Button
          style={{ backgroundColor: '#e31818' }}
          type="button"
          icon="pi pi-plus"
          label="Pridať priradenie k poradcovi"
          className="p-button-success"
          onClick={() => openDialog({})}
        />
        <div></div>
      </div>
    );
  };

  const onCellEditComplete = event => {
    const updatedPayments = [...agentProvisions];
    updatedPayments[event.rowIndex][event.field] = event.newValue;
    setAgentProvisions(updatedPayments);
  };

  const addOrUpdatePaymentContract = () => {
    setLoading(true);

    let call: Promise<any>;

    if (editAgentProvision.id) {
      call = updateAgentProvision(editAgentProvision.id, editAgentProvision);
    } else {
      call = addAgentProvision(provision.id, editAgentProvision);
    }

    call
      .then(response => {
        setResponseToData(response);
        setShowAgentProvisionDialog(false);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const dialogFooter = (
    <div>
      <Button
        style={{ backgroundColor: 'gray', marginRight: '10px' }}
        className="p-button-secondary"
        label="Zavrieť"
        icon="pi pi-times"
        onClick={() => setShowAgentProvisionDialog(false)}
      />
      <Button style={{ backgroundColor: '#e31818' }} className="p-button-primary" label="Uložiť" onClick={addOrUpdatePaymentContract} />
    </div>
  );

  const showExceedsLimitMessage = () => {
    if (provision?.provisionsExceedLimit) {
      return <h5 style={{ color: 'red' }}>!!! Do siete sa dá vyššia suma ako sa reálne dostane !!!</h5>;
    }
  };

  const confirmDeleteContact = contractToDelete => {
    setEditAgentProvision(contractToDelete);
    setDeleteProvisionDialog(true);
  };

  const deleteAgentProvisionFromImportedProvision = () => {
    setLoading(true);

    deleteAgentProvision(editAgentProvision.id)
      .then(response => {
        setResponseToData(response);
        setDeleteProvisionDialog(false);
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', e.response.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const deleteAgentProvisionDialogFooter = (
    <React.Fragment>
      <Button label="Nie" icon="pi pi-times" outlined onClick={() => setDeleteProvisionDialog(false)} />
      <Button label="Áno" icon="pi pi-check" severity="danger" onClick={deleteAgentProvisionFromImportedProvision} />
    </React.Fragment>
  );

  const actionBodyTemplate = rowData => {
    return (
      <React.Fragment>
        <div className="flex justify-content-around">
          {<Button icon="pi pi-pencil" rounded outlined className="mr-2" onClick={() => openDialog(rowData)} />}
          {<Button icon="pi pi-trash" rounded outlined severity="danger" onClick={() => confirmDeleteContact(rowData)} />}
        </div>
      </React.Fragment>
    );
  };

  const uzavreteTymtoPoradcom = rowData => {
    return <Checkbox style={{ marginLeft: '1rem' }} disabled={true} checked={rowData.concludedByAgent as boolean} />;
  };

  const typProviziePodlaConcludedBy = rowData => {
    if (rowData?.type === 1) {
      return 'získateľská';
    } else if (rowData?.type === 2) {
      return 'rozdielová';
    }

    return '';
  };

  const header = renderHeader();

  return (
    <>
      <div className="card">
        {loading && (
          <div className="flex justify-content-center">
            <ProgressSpinner />
          </div>
        )}
        {!loading && (
          <>
            <h2>{id ? `Upraviť províziu (${id})` : 'Vytvoriť novú províziu'}</h2>
            {showExceedsLimitMessage()}
            <Formik
              initialValues={
                provision
                  ? provision
                  : {
                      type: null,
                      risk: null,
                      from: null,
                      contract: null,
                      to: null,
                      zaklad: null,
                      provizia: null,
                      doba: null,
                    }
              }
              validationSchema={validationSchema}
              onSubmit={onEvaluate}
            >
              {({ values, setFieldValue, errors }) => {
                return (
                  <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
                    <div className="grid">
                      <div className="col-12 md:col-3">
                        <div className="p-field">
                          <label htmlFor="processed">Spracovaná provízia:</label>
                          <Checkbox
                            style={{ marginLeft: '1rem' }}
                            onChange={e => void setFieldValue(`processed`, e.checked)}
                            checked={values.processed as boolean}
                          />
                        </div>
                      </div>
                      <div className="col-12 md:col-5">
                        <div className="p-field">
                          <label htmlFor={`type`}>Typ provizie</label>
                          <Dropdown
                            id={`type`}
                            name={`type`}
                            value={values?.type}
                            options={provisionTypes}
                            optionLabel="label"
                            onChange={e => void setFieldValue(`type`, e.value)}
                            placeholder="Vyberte si typ provizie"
                          />
                          <ErrorMessage name="type" component="div" className="p-error" />
                        </div>
                      </div>
                      <div className="col-12 md:col-4">
                        <div className="p-field">
                          <label htmlFor={`risk`}>Riziko</label>
                          <Dropdown
                            id={`risk`}
                            name={`risk`}
                            value={values?.risk}
                            options={riskTypes}
                            optionLabel="label"
                            onChange={e => void setFieldValue(`risk`, e.value)}
                            placeholder="Vyberte si riziko"
                          />
                          <ErrorMessage name="risk" component="div" className="p-error" />
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div className="p-field">
                          <label htmlFor="from">Obdobie od</label>
                          <DateTextInput
                            id="from"
                            value={values.from}
                            onChange={val => void setFieldValue('from', val)}
                          />
                          <ErrorMessage name="from" component="div" className="p-error" />
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div className="p-field">
                          <label htmlFor="to">Obdobie do</label>
                          <DateTextInput
                            id="to"
                            value={values.to}
                            onChange={val => void setFieldValue('to', val)}
                          />
                          <ErrorMessage name="to" component="div" className="p-error" />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="p-field">
                          <label htmlFor="contract">Zmluva</label>
                          <ContractWidget
                            id="contract"
                            value={values.contract?.id}
                            onContractChanged={e => void setFieldValue('contract', { id: e })}
                          />
                          <ErrorMessage name="contract" component="div" className="p-error" />
                        </div>
                      </div>

                      <div className="col-12 md:col-4">
                        <div className="p-field">
                          <label htmlFor={`zaklad`}>Základ</label>
                          <InputNumber
                            id={`zaklad`}
                            value={provision?.zaklad}
                            locale="sk"
                            minFractionDigits={0}
                            maxFractionDigits={2}
                            onValueChange={e => void setFieldValue(`zaklad`, e.value)}
                          />
                          <ErrorMessage name={`zaklad`} component="div" className="p-error" />
                        </div>
                      </div>

                      <div className="col-12 md:col-4">
                        <div className="p-field">
                          <label htmlFor={`percento`}>Percento</label>
                          <InputNumber
                            id={`percento`}
                            value={provision?.percento}
                            locale="sk"
                            minFractionDigits={0}
                            maxFractionDigits={5}
                            onValueChange={e => void setFieldValue(`percento`, e.value)}
                          />
                          <ErrorMessage name={`percento`} component="div" className="p-error" />
                        </div>
                      </div>

                      <div className="col-12 md:col-4">
                        <div className="p-field">
                          <label htmlFor={`provizia`}>Provízia</label>
                          <InputNumber
                            id={`provizia`}
                            value={provision?.provizia}
                            locale="sk"
                            minFractionDigits={0}
                            maxFractionDigits={5}
                            onValueChange={e => void setFieldValue(`provizia`, e.value)}
                          />
                          <ErrorMessage name={`provizia`} component="div" className="p-error" />
                        </div>
                      </div>

                      <div className="col-12 md:col-4">
                        <div className="p-field">
                          <label htmlFor={`rocne`}>Ročne</label>
                          <InputNumber
                            id={`rocne`}
                            value={provision?.rocne}
                            locale="sk"
                            minFractionDigits={0}
                            maxFractionDigits={5}
                            onValueChange={e => void setFieldValue(`rocne`, e.value)}
                          />
                          <ErrorMessage name={`rocne`} component="div" className="p-error" />
                        </div>
                      </div>

                      <div className="col-12 md:col-4">
                        <div className="p-field">
                          <label htmlFor={`splatky`}>Splátky</label>
                          <InputNumber
                            id={`splatky`}
                            value={provision?.splatky}
                            locale="sk"
                            minFractionDigits={0}
                            maxFractionDigits={5}
                            onValueChange={e => void setFieldValue(`splatky`, e.value)}
                          />
                          <ErrorMessage name={`splatky`} component="div" className="p-error" />
                        </div>
                      </div>

                      <div className="col-12 md:col-4">
                        <div className="p-field">
                          <label htmlFor={`doba`}>Doba</label>
                          <InputNumber
                            id={`doba`}
                            value={provision?.doba}
                            locale="sk"
                            minFractionDigits={0}
                            maxFractionDigits={5}
                            onValueChange={e => void setFieldValue(`doba`, e.value)}
                          />
                          <ErrorMessage name={`doba`} component="div" className="p-error" />
                        </div>
                      </div>

                      <div className="col-12 md:col-6">
                        <div className="p-field">
                          <label htmlFor="warnings">Upozornenia</label>
                          <InputTextarea
                            id="warnings"
                            value={values.warnings}
                            rows={2}
                            autoResize
                            onChange={e => void setFieldValue('warnings', e.target.value)}
                            placeholder="Upozornenia"
                          />
                        </div>
                      </div>
                      <div className="col-12 md:col-6">
                        <div className="p-field">
                          <label htmlFor="errors">Chyby</label>
                          <InputTextarea
                            id="errors"
                            value={values.errors}
                            rows={2}
                            autoResize
                            onChange={e => void setFieldValue('errors', e.target.value)}
                            placeholder="Chyby"
                          />
                        </div>
                      </div>

                      <div className="col-6">
                        <Button
                          className="p-button-secondary"
                          onClick={() => handleSubmit(values)}
                          disabled={loading}
                          type="button"
                          style={{ backgroundColor: '#e31818' }}
                          label="Uložiť"
                        />
                      </div>
                      <div className="col-6">
                        <Button
                          style={{ backgroundColor: '#e31818' }}
                          disabled={loading}
                          type="submit"
                          label="Uložiť a spracovať províziu"
                        />
                      </div>
                      <div className="col-12">
                        <Button
                          label="Späť"
                          type="button"
                          icon="pi pi-arrow-left"
                          className="p-button-secondary mr-2"
                          style={{ backgroundColor: 'gray' }}
                          onClick={() => onCancel()}
                        />
                      </div>
                    </div>
                  </Form>
                );
              }}
            </Formik>{' '}
          </>
        )}
        {editAgentProvision && (
          <Dialog
            visible={showAgentProvisionDialog}
            header="Spracovaná provízia pre poradcu"
            style={{ width: '80vw' }}
            footer={dialogFooter}
            onHide={() => setShowAgentProvisionDialog(false)}
          >
            {/* InputText for user query */}
            <div className="p-fluid grid">
              <div className="col-3">
                <div className="p-field">
                  <label htmlFor="isStorno">Je to storno:</label>
                  <Checkbox
                    style={{ marginLeft: '1rem' }}
                    checked={editAgentProvision.isStorno}
                    onChange={e => setEditAgentProvision({ ...editAgentProvision, isStorno: e.checked })}
                  />
                </div>
              </div>
              <div className="col-3">
                <div className="p-field">
                  <label htmlFor="concludedByAgent">Zmluva uzavretá týmto poradcom:</label>
                  <Checkbox
                    style={{ marginLeft: '1rem' }}
                    checked={editAgentProvision.concludedByAgent}
                    onChange={e => setEditAgentProvision({ ...editAgentProvision, concludedByAgent: e.checked })}
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="p-field">
                  <label htmlFor="agent">Poradca:</label>
                  <AgentWidget
                    id="agent"
                    onCareerChanged={() => {}}
                    showCareer={false}
                    value={editAgentProvision.agent?.id}
                    onPersonChanged={e => setEditAgentProvision({ ...editAgentProvision, agent: { id: e } })}
                  />
                </div>
              </div>

              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="nrOfUnits">Počet jednotiek:</label>
                  <InputNumber
                    id="nrOfUnits"
                    value={editAgentProvision.nrOfUnits}
                    locale="sk"
                    minFractionDigits={0}
                    maxFractionDigits={6}
                    onValueChange={e => setEditAgentProvision({ ...editAgentProvision, nrOfUnits: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="unitValue">Hodnota jednotky:</label>
                  <InputNumber
                    id="unitValue"
                    value={editAgentProvision.unitValue}
                    locale="sk"
                    minFractionDigits={0}
                    maxFractionDigits={2}
                    onValueChange={e => setEditAgentProvision({ ...editAgentProvision, unitValue: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-4">
                <div className="p-field">
                  <label htmlFor="provision">Provízia:</label>
                  <InputNumber
                    id="provision"
                    value={editAgentProvision.provision}
                    locale="sk"
                    minFractionDigits={0}
                    maxFractionDigits={4}
                    onValueChange={e => setEditAgentProvision({ ...editAgentProvision, provision: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/*<div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>*/}
            {/*  <Button label="Vytvoriť novú osobu" type="button" onClick={() => onOpenCreateModal(null)} size="large" />*/}
            {/*</div>*/}
          </Dialog>
        )}
        <Dialog
          visible={deleteProvisionDialog}
          style={{ width: '32rem' }}
          breakpoints={{ '960px': '75vw', '641px': '90vw' }}
          header="Potvrdenie"
          modal
          footer={deleteAgentProvisionDialogFooter}
          onHide={() => setDeleteProvisionDialog(false)}
        >
          <div className="confirmation-content">
            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
            {editAgentProvision?.agentName && (
              <span>
                Naozaj chcete vymazať priradenú províziu poradcovi: <b>{editAgentProvision.agentName}</b>?
              </span>
            )}
          </div>
        </Dialog>
      </div>
      {provision?.contract?.id && (
        <div className="card">
          <div>
            <h4>Spracovaná provízia priradená k:</h4>
            {showExceedsLimitMessage()}
            <DataTable
              stripedRows
              value={agentProvisions}
              size="small"
              lazy
              responsiveLayout="scroll"
              dataKey="id"
              emptyMessage="Žiadne dáta."
              header={header}
              filterDisplay="menu"
              loading={loading}
              editMode="cell"
              // onRowEditComplete={onRowEditComplete}
              className="table-row-with-line"
            >
              <Column field="agentName" header="Meno poradcu" />
              <Column field="concludedByAgent" header="Uzavrel zmluvu" body={v => uzavreteTymtoPoradcom(v)} />
              <Column field="positionName" header="Pozícia" />
              <Column field="unitValue" header="Hodnota J za pozíciu" />
              <Column field="type" header="Typ provízie" body={v => typProviziePodlaConcludedBy(v)} />
              <Column field="nrOfUnits" header="Počet jednotiek" />
              <Column field="provision" header="Provízia" />
              <Column field="period" header="Doba" />
              <Column header="Akcie" body={actionBodyTemplate}></Column>
            </DataTable>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateProvision;
